import { alpha2ToAlpha3, subdivisionsForCountry } from "@connect/i18n";
import type { components } from "@connect/data/frames/schema";
import type { StructuredAddress } from "@connect/component-library/components/address-autocomplete";

type CustomerAddress = NonNullable<
  components["schemas"]["CurrentCustomerReturnType"]["customer"]["address"]
>;

// Maps the customer's saved residential address (from /customer/current) onto the
// billing form's StructuredAddress. Returns null when there's nothing worth
// prefilling. Country is normalised to alpha-3 (input may be alpha-2 or alpha-3),
// and the state is matched against the country's subdivisions so the State
// dropdown resolves to a known {code, name}.
export function mapCustomerAddress(
  address: CustomerAddress,
  fallbackCountry: string,
): StructuredAddress | null {
  const street = address.street ?? "";
  const town = address.locality ?? "";
  const postCode = address.postalCode ?? "";
  if (!street && !town && !postCode) {
    return null;
  }

  const alpha3 = alpha2ToAlpha3(address.country ?? fallbackCountry);
  const rawState = address.administrativeArea ?? undefined;
  const match = rawState
    ? subdivisionsForCountry(alpha3).find((s) => s.code === rawState || s.name === rawState)
    : undefined;

  return {
    street,
    ...(address.subStreet ? { subStreet: address.subStreet } : {}),
    town,
    postCode,
    ...(rawState
      ? { state: { code: match?.code ?? rawState, name: match?.name ?? rawState } }
      : {}),
    country: { alpha3, name: "" },
  };
}
